/**
 * Shared loader for the Salary Playground payload (rosters, prices, retention rules).
 *
 * Used by the Salary Playground and the Trade Scorecard so both read the same
 * browser cache and the same backend data. Bump DATA_CACHE_VERSION when the API
 * payload changes shape; entries also expire by age. `force` bypasses the browser
 * cache and the backend's in-process cache.
 */
import { apiService } from '../services/api';
import { SalaryPlaygroundResponse } from '../types/lineup';
import { BasePlayer, SalaryReference } from './salaryEngine';

export const DATA_CACHE_KEY = 'salary_playground_data';
export const DATA_CACHE_VERSION = 4; // 2: overrides; 3: team colours; 4: salary season 2027 + shared overrides
export const DATA_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export const readSalaryCache = (): SalaryPlaygroundResponse | null => {
  try {
    const raw = window.localStorage.getItem(DATA_CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || c.v !== DATA_CACHE_VERSION || Date.now() - c.ts > DATA_CACHE_TTL_MS || !c.data?.players?.length) return null;
    return c.data as SalaryPlaygroundResponse;
  } catch {
    return null;
  }
};

export const writeSalaryCache = (data: SalaryPlaygroundResponse) => {
  try {
    window.localStorage.setItem(DATA_CACHE_KEY, JSON.stringify({ v: DATA_CACHE_VERSION, ts: Date.now(), data }));
  } catch (error) {
    console.warn('Could not cache salary data:', error);
  }
};

/** Cached payload if fresh, otherwise fetched (and cached). `force` always fetches. */
export async function loadSalaryData(force = false): Promise<SalaryPlaygroundResponse> {
  if (!force) {
    const cached = readSalaryCache();
    if (cached) return cached;
  }
  const res = await apiService.getSalaryPlayground('IPL', force);
  writeSalaryCache(res);
  return res;
}

/** Shape the payload into the salary engine's inputs. */
export function toEngineInputs(data: SalaryPlaygroundResponse): { base: Record<string, BasePlayer>; ref: SalaryReference } {
  const teams: SalaryReference['teams'] = {};
  data.teams.forEach((t) => {
    teams[t.code] = { code: t.code, name: t.name, color: t.color };
  });
  const retained: SalaryReference['retained'] = {};
  data.retained.forEach((r) => {
    retained[r.player_name] = [r.team, r.capped ? 'C' : 'U'];
  });
  const traded: SalaryReference['traded'] = {};
  data.traded.forEach((t) => {
    traded[t.player_name] = { from: t.from_team, to: t.to_team, when: t.when, was: t.was_price };
  });
  const guide: Record<number, number> = {};
  Object.entries(data.retention_guideline).forEach(([k, v]) => {
    guide[Number(k)] = v;
  });
  const ref: SalaryReference = {
    teams,
    codes: Object.keys(teams).sort(),
    retained,
    guide,
    traded,
    natUnverified: new Set(data.nationality_unverified),
    defaults: { cap: data.defaults.cap, maxSquad: data.defaults.max_squad, maxOS: data.defaults.max_overseas },
  };
  const base: Record<string, BasePlayer> = {};
  data.players.forEach((p) => {
    base[String(p.player_id)] = { id: String(p.player_id), name: p.player_name, team: p.team, price: p.price, nat: p.nationality, custom: false };
  });
  return { base, ref };
}
